import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { queryClient } from '@/lib/queryClient';

interface User {
  id: string;
  email: string;
  name: string;
  avatarUrl?: string;
  organizations?: Array<{
    id: string;
    name: string;
    slug: string;
    role: string;
    type: 'AGENCY' | 'CLIENT';
  }>;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  activeOrgId: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  _hasHydrated: boolean;

  setAuth: (accessToken: string, user?: User) => void;
  setTokens: (accessToken: string, refreshToken: string | null) => void;
  clearAuth: () => void;
  logout: (options?: { skipOidcLogout?: boolean }) => void;
  setUser: (user: User) => void;
  setActiveOrg: (orgId: string) => void;
  setLoading: (loading: boolean) => void;
  setHasHydrated: (state: boolean) => void;
}

/** Pick a valid active org: keep current if still a member, else first org. */
function resolveActiveOrg(user: User | null | undefined, current: string | null): string | null {
  const orgs = user?.organizations ?? [];
  if (orgs.length === 0) return null;
  if (current && orgs.some((o) => o.id === current)) return current;
  return orgs[0].id;
}

const OIDC_AUTH_PORT = 8080;

function getOidcIssuerUrl(): string {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `http://127.0.0.1:${OIDC_AUTH_PORT}`;
  }
  if (/^(\d{1,3}\.){3}\d{1,3}$/.test(hostname)) {
    return `http://${hostname}:${OIDC_AUTH_PORT}`;
  }
  const parts = hostname.split('.');
  if (parts.length >= 2) {
    parts[0] = 'auth';
    return `https://${parts.join('.')}`;
  }
  return `http://127.0.0.1:${OIDC_AUTH_PORT}`;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      activeOrgId: null,
      isAuthenticated: false,
      isLoading: false,
      _hasHydrated: false,

      setAuth: (accessToken, user) => {
        set({
          accessToken,
          user: user || null,
          activeOrgId: resolveActiveOrg(user, get().activeOrgId),
          isAuthenticated: true,
          isLoading: false,
        });
      },

      // Token refresh: replaces both tokens (refresh tokens rotate server-side).
      setTokens: (accessToken, refreshToken) => set({ accessToken, refreshToken }),

      // Clear auth state without navigating — for the 401/refresh-failed path,
      // which redirects to OIDC login itself (preserving the current deep link).
      clearAuth: () => {
        queryClient.clear();
        localStorage.removeItem('solid-sales-auth');
        localStorage.removeItem('solid-hub-auth');
        sessionStorage.removeItem('oidc_code_verifier');
        sessionStorage.removeItem('oidc_state');
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
        });
      },

      logout: (options?: { skipOidcLogout?: boolean }) => {
        queryClient.clear();

        localStorage.removeItem('solid-sales-auth');
        localStorage.removeItem('solid-hub-auth');
        sessionStorage.removeItem('oidc_code_verifier');
        sessionStorage.removeItem('oidc_state');

        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
        });

        if (options?.skipOidcLogout) {
          window.location.href = window.location.origin;
          return;
        }

        const oidcIssuer = getOidcIssuerUrl();
        const logoutUrl = new URL(`${oidcIssuer}/oauth/logout`);
        logoutUrl.searchParams.set('client_id', 'solid-sales');
        logoutUrl.searchParams.set('post_logout_redirect_uri', window.location.origin);

        window.location.href = logoutUrl.toString();
      },

      setUser: (user) => {
        set({ user, activeOrgId: resolveActiveOrg(user, get().activeOrgId) });
      },

      setActiveOrg: (orgId) => {
        const orgs = get().user?.organizations ?? [];
        if (!orgs.some((o) => o.id === orgId)) return; // ignore orgs the user isn't in
        set({ activeOrgId: orgId });
        queryClient.clear(); // drop cached data scoped to the previous org
      },

      setLoading: (loading) => {
        set({ isLoading: loading });
      },

      setHasHydrated: (state) => {
        set({ _hasHydrated: state });
      },
    }),
    {
      name: 'solid-sales-auth',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        activeOrgId: state.activeOrgId,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    }
  )
);
